import React, { useState, useEffect } from 'react';
import { FolderOpen, Plus, Edit2, Trash2, MapPin, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectModal = ({ project, onSave, onClose }) => {
  const [form, setForm] = useState(project || {
    title: '', location: '', type: '', status: 'Active', units: 0, 
    strategy: 'Buy-to-Let', image: '', timeline: '', roi: '', description: ''
  });

  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23D4AF37'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '20px'
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-[#0a1a14] border border-white/10 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg text-white mb-4">{project ? 'Edit Project' : 'Add Project'}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-white/50 text-sm block mb-1">Title *</label>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Location *</label>
            <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Type</label>
            <Input value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="Residential, Commercial..." />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Strategy</label>
            <select 
              value={form.strategy} 
              onChange={(e) => setForm({...form, strategy: e.target.value})}
              className="w-full bg-[#1a2a24] border border-white/10 text-white rounded-md p-2 appearance-none cursor-pointer"
              style={selectStyle}
            >
              <option value="Strategic & Creative Acquisitions" className="bg-[#1a2a24] text-white">Strategic &amp; Creative Acquisitions</option>
              <option value="Development & Value Creation" className="bg-[#1a2a24] text-white">Development &amp; Value Creation</option>
              <option value="Investment Property" className="bg-[#1a2a24] text-white">Investment Property</option>
            </select>
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Status</label>
            <select 
              value={form.status} 
              onChange={(e) => setForm({...form, status: e.target.value})}
              className="w-full bg-[#1a2a24] border border-white/10 text-white rounded-md p-2 appearance-none cursor-pointer"
              style={selectStyle}
            >
              <option value="Active" className="bg-[#1a2a24] text-white">Active</option>
              <option value="Completed" className="bg-[#1a2a24] text-white">Completed</option>
              <option value="Planning" className="bg-[#1a2a24] text-white">Planning</option>
              <option value="On Hold" className="bg-[#1a2a24] text-white">On Hold</option>
              <option value="Sold" className="bg-[#1a2a24] text-white">Sold</option>
            </select>
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Units</label>
            <Input type="number" value={form.units} onChange={(e) => setForm({...form, units: parseInt(e.target.value) || 0})} className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Timeline</label>
            <Input value={form.timeline} onChange={(e) => setForm({...form, timeline: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="e.g., 12 months" />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">ROI</label>
            <Input value={form.roi} onChange={(e) => setForm({...form, roi: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="e.g., 15%" />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">Image URL</label>
            <Input value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="https://..." />
          </div>
          <div className="col-span-2">
            <label className="text-white/50 text-sm block mb-1">Description</label>
            <textarea 
              value={form.description} 
              onChange={(e) => setForm({...form, description: e.target.value})} 
              className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2 min-h-[80px]"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button onClick={() => onSave({...form, id: project?.id})} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]">
            {project ? 'Update' : 'Add'} Project
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ProjectsTab = ({ projects, saveProject, deleteProject, fetchAllData, getStoredPassword }) => {
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/projects-settings?password=${getStoredPassword()}`);
        if (res.ok) {
          const data = await res.json();
          setSectionEnabled(data.enabled !== false);
        }
      } catch (e) {
        console.error('Failed to fetch projects settings');
      }
      setLoading(false);
    };
    fetchSettings();
  }, [getStoredPassword]);

  const toggleSection = async () => {
    const newValue = !sectionEnabled;
    try {
      const res = await fetch(`${API_URL}/api/admin/projects-settings?enabled=${newValue}&password=${getStoredPassword()}`, {
        method: 'POST'
      });
      if (res.ok) {
        setSectionEnabled(newValue);
        toast.success(newValue ? 'Projects section visible' : 'Showing "Coming Soon"');
      }
    } catch (e) {
      toast.error('Failed to update settings');
    }
  };

  const handleSave = async (project) => {
    await saveProject(project);
    setShowModal(false);
    setEditing(null);
  };

  if (loading) return <div className="text-white/50 text-center py-8">Loading...</div>;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg text-white">Projects</h3>
            <p className="text-white/40 text-sm">Manage your project portfolio displayed on the website</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-white/50 text-sm">{sectionEnabled ? 'Visible' : 'Coming Soon'}</span>
              <button
                onClick={toggleSection}
                data-testid="projects-section-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  sectionEnabled ? 'bg-green-500' : 'bg-[#D4AF37]'
                }`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  sectionEnabled ? 'left-7' : 'left-1'
                }`} />
              </button>
            </div>
            {projects.length === 0 && (
              <Button 
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/admin/projects/populate-defaults?password=${getStoredPassword()}`, { method: 'POST' });
                    if (res.ok) {
                      toast.success('Default projects added');
                      fetchAllData();
                    }
                  } catch (e) {
                    toast.error('Failed to populate projects');
                  }
                }}
                variant="outline"
                className="border-white/10 text-white/60"
              >
                Populate Defaults
              </Button>
            )}
            <Button 
              onClick={() => { setEditing(null); setShowModal(true); }}
              className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
              data-testid="add-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Project
            </Button>
          </div>
        </div>

        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-4 ${
          sectionEnabled ? 'bg-green-500/20 text-green-400' : 'bg-[#D4AF37]/20 text-[#D4AF37]'
        }`}>
          {sectionEnabled ? (
            <><CheckCircle className="w-3 h-3" /> Projects visible on site</>
          ) : (
            <><Clock className="w-3 h-3" /> Showing "Coming Soon" on site</>
          )}
        </div>

        {projects.length === 0 ? (
          <div className="text-center text-white/50 py-8">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No projects yet.</p>
            <p className="text-sm mt-1">Click "Populate Defaults" to add sample projects, or add your own.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((project) => (
              <div key={project.id} className={`bg-white/5 border rounded-lg p-4 ${project.visible === false ? 'border-white/5 opacity-50' : 'border-white/10'}`}>
                <div className="flex items-start gap-4">
                  {project.image && (
                    <div className="w-24 h-16 rounded overflow-hidden flex-shrink-0">
                      <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-white font-medium">{project.title}</h4>
                        <p className="text-white/50 text-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {project.location}
                        </p>
                        <div className="flex gap-2 mt-2 flex-wrap">
                          <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1 rounded">{project.strategy}</span>
                          <span className="text-xs bg-white/10 text-white/60 px-2 py-1 rounded">{project.status}</span>
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">{project.roi}</span>
                          {project.visible === false && (
                            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">Hidden</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            const updated = {...project, visible: project.visible === false ? true : false};
                            fetch(`${API_URL}/api/admin/projects/${project.id}?password=${getStoredPassword()}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(updated)
                            }).then(res => {
                              if (res.ok) {
                                toast.success(updated.visible ? 'Project visible' : 'Project hidden');
                                fetchAllData();
                              }
                            });
                          }}
                          className={`p-1 hover:bg-white/10 rounded ${project.visible === false ? 'text-yellow-400' : 'text-white/40'}`}
                          title={project.visible === false ? 'Show project' : 'Hide project'}
                        >
                          {project.visible === false ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </button>
                        <button onClick={() => { setEditing(project); setShowModal(true); }} className="p-1 hover:bg-white/10 rounded">
                          <Edit2 className="w-4 h-4 text-white/60" />
                        </button>
                        <button onClick={() => deleteProject(project.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <ProjectModal
          project={editing}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </>
  );
};
